import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Button,
  EmptyState,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatClockTime, formatDate } from "../lib/format";
import { formatMinutes, isManagerOrAbove } from "../lib/people";
import { registerQuery, registerQueryKey } from "../lib/attendance";

export function AttendancePage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const isManager = isManagerOrAbove(user?.role);
  // The filter lives in the URL so the register can be linked to, reloaded and
  // bookmarked with the same employee selected.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmployee = searchParams.get("employee");

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 5 * 60_000,
  });

  // Employees never choose a colleague: their own record is the only one the
  // backend will return, so select it automatically once it is known.
  const autoEmployee = !isManager ? employees.data?.[0]?.id ?? null : null;
  const effectiveEmployee = selectedEmployee ?? autoEmployee;

  // Nothing is fetched until a person is chosen: the register is a per-employee
  // view, never a combined company list.
  const query = useInfiniteQuery({
    queryKey: registerQueryKey(effectiveEmployee),
    queryFn: ({ signal, pageParam }) =>
      api.attendancePage(
        {
          ...registerQuery(effectiveEmployee),
          cursor: pageParam || undefined,
        },
        signal,
      ),
    enabled: Boolean(effectiveEmployee),
    initialPageParam: "",
    getNextPageParam: (lastPage) => cursorFrom(lastPage.next),
  });

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.results) ?? [],
    [query.data],
  );

  const selectedPerson = useMemo(
    () => (employees.data ?? []).find((person) => person.id === effectiveEmployee),
    [employees.data, effectiveEmployee],
  );

  const selectEmployee = (employeeId: string) => {
    // Selecting a person replaces the view; it never falls back to "everyone".
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("employee", employeeId);
      return next;
    });
  };

  return (
    <>
      <PageHeader
        title="Attendance"
        subtitle="Daily work, learning and attendance details by employee."
      />

      {isManager ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {(employees.data ?? []).map((person) => (
            <button
              key={person.id}
              type="button"
              aria-pressed={effectiveEmployee === person.id}
              onClick={() => selectEmployee(person.id)}
              className={cardClass(effectiveEmployee === person.id)}
            >
              <span className="block text-sm font-medium">{person.display_name}</span>
              <span
                className={`block text-xs ${
                  effectiveEmployee === person.id ? "text-slate-300" : "text-slate-500"
                }`}
              >
                {person.employee_code}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {employees.isError ? (
        <EmptyState
          title="Could not load employees"
          body={
            employees.error instanceof ApiError
              ? employees.error.message
              : "Please try again."
          }
        />
      ) : null}

      {effectiveEmployee ? (
        <>
          {selectedPerson ? (
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              {selectedPerson.display_name} · {selectedPerson.employee_code}
            </p>
          ) : null}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
            {query.isPending ? (
              <TableSkeleton rows={8} />
            ) : query.isError ? (
              <EmptyState
                title="Could not load attendance"
                body={
                  query.error instanceof ApiError
                    ? query.error.message
                    : "Please try again."
                }
              />
            ) : rows.length === 0 ? (
              <EmptyState
                title="No attendance records"
                body="Attendance records with work and learning will appear here."
              />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5">Date &amp; Attendance</th>
                    <th className="px-4 py-2.5">Work Done Today</th>
                    <th className="px-4 py-2.5">Learnings Today</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="align-top hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-slate-900">
                          {formatDate(row.work_date, timezone ?? undefined)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatWeekday(row.work_date, timezone ?? undefined)}
                        </div>
                        <div className="mt-1 text-xs text-slate-600">
                          In:{" "}
                          {row.clock_in_at
                            ? formatClockTime(row.clock_in_at, timezone ?? undefined)
                            : "Not checked in"}
                        </div>
                        <div className="text-xs text-slate-600">
                          Out:{" "}
                          {row.clock_out_at
                            ? formatClockTime(row.clock_out_at, timezone ?? undefined)
                            : "Not checked out"}
                        </div>
                        <div className="text-xs text-slate-600">
                          Duration:{" "}
                          {row.worked_minutes != null
                            ? formatMinutes(row.worked_minutes)
                            : row.clock_in_at
                              ? "In progress"
                              : "—"}
                        </div>
                        <div className="mt-1">
                          <BadgeForStatus status={row.status} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <ReportText text={row.work_done} />
                      </td>
                      <td className="px-4 py-3">
                        <ReportText text={row.learning} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-slate-500">
                Showing {rows.length} record{rows.length === 1 ? "" : "s"}
                {query.hasNextPage ? " so far" : ""}.
              </p>
              {query.hasNextPage ? (
                <Button
                  variant="ghost"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-xs">
          <EmptyState
            title={isManager ? "Select an employee" : "No attendance records yet"}
            body={
              isManager
                ? "Select an employee to view attendance, daily work and learnings."
                : "Your attendance records will appear here after you check in on My Desk."
            }
          />
        </div>
      )}

      {isManager ? (
        <p className="mt-4 text-xs text-slate-500">
          Select an employee card to switch the register. Managers see only the
          records they are authorized to access.
        </p>
      ) : null}
    </>
  );
}

function formatWeekday(iso: string, timeZone?: string): string {
  if (!iso) return "";
  const calendar = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const parsed = calendar ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timeZone ?? undefined,
    weekday: "short",
  });
  return formatter.format(parsed);
}

function BadgeForStatus({ status }: { status?: string | null }) {
  const tone =
    status === "present"
      ? "emerald"
      : status === "absent"
        ? "rose"
        : "amber";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        tone === "emerald"
          ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
          : tone === "rose"
            ? "bg-rose-50 text-rose-700 ring-rose-600/20"
            : "bg-amber-50 text-amber-700 ring-amber-600/20"
      }`}
    >
      {(status ?? "present").replace(/_/g, " ")}
    </span>
  );
}

function ReportText({ text }: { text?: string | null }) {
  if (!text || !text.trim()) {
    return <span className="text-slate-400">—</span>;
  }
  const lines = text.split(/\r?\n/);
  const isList = lines.some((line) => /^\s*[-*•]/.test(line));
  if (isList) {
    return (
      <ul className="list-disc space-y-1 pl-4 text-sm text-slate-700">
        {lines
          .filter((line) => line.trim())
          .map((line, index) => (
            <li key={index}>{line.replace(/^\s*[-*•]\s*/, "")}</li>
          ))}
      </ul>
    );
  }
  return <p className="whitespace-pre-wrap text-sm text-slate-700">{text}</p>;
}

function cardClass(selected: boolean): string {
  return [
    "min-w-[140px] rounded-lg border px-3 py-2 text-left transition",
    selected
      ? "border-slate-900 bg-slate-900 text-white"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
  ].join(" ");
}

function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  const base =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL(next, base).searchParams.get("cursor") ?? undefined;
}
